/** Prefer .webp sibling when catalog stored a heavy png/jpeg (common on older product uploads). */
export function preferWebpVariantUrl(url: string): string {
  try {
    const parsed = new URL(url);
    // Presigned S3 URLs must not be rewritten — signature is bound to the object key.
    if (
      parsed.hostname.includes('amazonaws.com') &&
      (parsed.search.includes('x-id=') || parsed.search.includes('X-Amz-Algorithm='))
    ) {
      return url;
    }
    if (/\.(png|jpe?g)$/i.test(parsed.pathname)) {
      parsed.pathname = parsed.pathname.replace(/\.(png|jpe?g)$/i, '.webp');
      return parsed.toString();
    }
  } catch {
    /* ignore */
  }
  return url;
}

/** Tune remote image URLs for faster loads (Unsplash supports w/q/auto params). */
export function optimizeRemoteImageUrl(url: string, width: number, quality = 75): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'images.unsplash.com') {
      parsed.searchParams.set('w', String(width));
      parsed.searchParams.set('q', String(quality));
      parsed.searchParams.set('auto', 'format');
      parsed.searchParams.set('fit', 'crop');
      return parsed.toString();
    }
  } catch {
    /* ignore invalid URLs */
  }
  return url;
}

export function isUnsplashUrl(url: string): boolean {
  try {
    return new URL(url).hostname === 'images.unsplash.com';
  } catch {
    return false;
  }
}

/** Hero display widths — cap below 1400px to match typical viewport (PSI ~787px). */
export const HERO_IMAGE_WIDTHS = [640, 960, 1280] as const;
export const HERO_SIZES = '100vw';

export function buildUnsplashSrcSet(
  url: string,
  widths: readonly number[] = HERO_IMAGE_WIDTHS,
): string {
  return widths.map((w) => `${optimizeRemoteImageUrl(url, w)} ${w}w`).join(', ');
}

/** Default intrinsic dimensions when layout is controlled via CSS (reduces CLS). */
export const IMAGE_DIMENSIONS = {
  square: { width: 400, height: 400 },
  hero: { width: 960, height: 384 },
  tile: { width: 224, height: 224 },
  propertyCard: { width: 480, height: 300 },
  classifiedGrid: { width: 480, height: 360 },
  classifiedThumb: { width: 128, height: 128 },
} as const;
